import { msg } from "@lingui/core/macro";
import dayjs from "dayjs";
import deepFreeze from "deep-freeze-es6";

type ActionOptions = {
  title?: string;
  isInReview?: boolean;
  comment?: string;
  assignee?: string;
  startDate?: dayjs.Dayjs;
  endDate?: dayjs.Dayjs;
  scope?: (typeof SCOPES)[number];
  emissionReduction?: Reduction;
  energyReduction?: Reduction;
  staticCostEfficiency?: StaticCostEfficiency;
  valeri?: Valeri;
  valeriResult?: ValeriResult;
  documents?: Array<string>;
};

type StaticCostEfficiency = {
  cost: number | undefined;
  savingsPerYear: number | undefined;
};

type AbsoluteReduction = {
  delta?: number;
};

export function isAbsoluteReduction(
  reduction: Reduction | undefined
): reduction is AbsoluteReduction {
  return (
    reduction !== undefined &&
    "delta" in reduction &&
    !("actual" in reduction) &&
    !("target" in reduction)
  );
}

export function isRelativeReduction(
  reduction: Reduction | undefined
): reduction is RelativeReduction {
  return (
    reduction !== undefined &&
    ("actual" in reduction || "target" in reduction) &&
    !("delta" in reduction)
  );
}

type RelativeReduction = {
  actual?: number;
  target?: number;
};

type Reduction = AbsoluteReduction | RelativeReduction;

export const SCENARIO_LABELS = {
  mostLikelyScenario: msg`Most Likely Scenario`,
  bestCaseScenario: msg`Best Case Scenario`,
  worstCaseScenario: msg`Worst Case Scenario`,
} as const;

type Valeri = {
  [K in keyof typeof SCENARIO_LABELS]: ValeriScenario;
};

type ValeriScenario = {
  equityShare?: number;
  equityInterestRate?: number;
  debtInterestRate?: number;
  plannedEconomicLifetime?: number;
  currentElectricityPrice?: number;
  annualElectricityPriceEscalation?: number;
  currentNaturalGasPrice?: number;
  annualNaturalGasPriceEscalation?: number;
  inflation?: number;
  expenditures?: number;
  annualElectricitySavings?: number;
  annualNaturalGasSavings?: number;
  annualOperationalSavings?: number;
  onetimeCashInflows?: number;
};

export type ValeriResult = {
  [K in keyof typeof SCENARIO_LABELS]: ValeriResultScenario;
};

export type ValeriResultScenario = {
  amortizationYear?: number;
  breakEvenRatio?: number;
  capitalValueEndOfLifetime: number;
  yearResults: Array<ValeriYearResult>;
};

export type ValeriYearResult = {
  capitalValue: number;
  cashValue: number;
  electricitySavings: number;
  expenditures: number;
  generalSavings: number;
  naturalGasSavings: number;
  onetimeCashInflow: number;
  period: number;
  totalSavings: number;
};

export class Action {
  readonly id: string;
  readonly status: ActionStatus;
  readonly title: string | undefined;
  readonly isInReview: boolean;
  readonly comment: string | undefined;
  readonly assignee: string | undefined;
  readonly startDate: dayjs.Dayjs | undefined;
  readonly endDate: dayjs.Dayjs | undefined;
  readonly scope: (typeof SCOPES)[number] | undefined;
  readonly emissionReduction: Reduction | undefined;
  readonly energyReduction: Reduction | undefined;
  readonly staticCostEfficiency: StaticCostEfficiency | undefined;
  readonly valeri: Valeri | undefined;
  readonly valeriResult: ValeriResult | undefined;
  readonly documents: Array<string>;
  readonly currency: string;
  private readonly _optionsCache: ActionOptions | undefined;

  constructor(id: string, status: ActionStatus, options?: ActionOptions) {
    this.id = id;
    this.status = status;
    this.title = options?.title;
    this.isInReview = options?.isInReview ?? false;
    this.comment = options?.comment;
    this.assignee = options?.assignee;
    this.startDate = options?.startDate;
    this.endDate = options?.endDate;
    this.scope = options?.scope;
    this.emissionReduction = options?.emissionReduction;
    this.energyReduction = options?.energyReduction;
    if (options) {
      if (options.staticCostEfficiency && options.valeri) {
        throw new Error("An action cannot have both static cost efficiency and VALERI options.");
      }
      if (options.staticCostEfficiency !== undefined) {
        this.staticCostEfficiency = options.staticCostEfficiency;
      } else if (options.valeri !== undefined) {
        this.valeri = options.valeri;
        this.valeriResult = options.valeriResult;
      }
    }
    this.documents = [...(options?.documents ?? [])];
    this.currency = "€";
    this._optionsCache = deepFreeze(options ?? {});
  }

  static calculateROI(cost?: number, savingsPerYear?: number) {
    if (cost && savingsPerYear) return (savingsPerYear / cost) * 100;
  }

  get ROI() {
    return Action.calculateROI(
      this.staticCostEfficiency?.cost,
      this.staticCostEfficiency?.savingsPerYear
    );
  }

  get amortizationTimeInYears() {
    return (
      Action.getStaticAmortizationTimeInYears(
        this.staticCostEfficiency?.cost,
        this.staticCostEfficiency?.savingsPerYear
      ) ?? this.valeriResult?.mostLikelyScenario.amortizationYear
    );
  }

  static getStaticAmortizationTimeInYears(
    cost: number | undefined,
    savingsPerYear: number | undefined
  ): number | undefined {
    return !!cost && !!savingsPerYear ? Math.round(cost / savingsPerYear) : undefined;
  }

  get emissionReductionDeltaInTons(): number | undefined {
    return isAbsoluteReduction(this.emissionReduction) && this.emissionReduction.delta !== undefined
      ? this.emissionReduction.delta / 1000
      : undefined;
  }

  get emissionReductionTargetInTons(): number | undefined {
    return isRelativeReduction(this.emissionReduction) &&
      this.emissionReduction.target !== undefined
      ? this.emissionReduction.target / 1000
      : undefined;
  }

  get emissionReductionActualInTons(): number | undefined {
    return isRelativeReduction(this.emissionReduction) &&
      this.emissionReduction.actual !== undefined
      ? this.emissionReduction.actual / 1000
      : undefined;
  }

  get impactYear(): number | undefined {
    return this.endDate ? this.endDate.year() + 1 : undefined;
  }

  hasStaticCostEfficiency(): this is { staticCostEfficiency: StaticCostEfficiency } {
    return this.staticCostEfficiency !== undefined;
  }

  hasValeri(): this is { valeri: Valeri; valeriResult: ValeriResult } {
    return this.valeri !== undefined;
  }

  changeStatus(newStatus: ActionStatus): Action {
    return new Action(this.id, newStatus, this._optionsCache);
  }
}

export const SCOPES = ["1", "2", "3"] as const;

export const ACTION_STATUS = ["backlog", "in_progress", "done"] as const;
export type ActionStatus = (typeof ACTION_STATUS)[number];

export function assertActionStatus(status: any): asserts status is ActionStatus {
  if (!ACTION_STATUS.includes(status)) {
    throw new TypeError("Not a status");
  }
}
